/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { UseOAuthHandlersHook } from '../../ai-answer-dialog/types';

import { messages } from './messages';
import type { ThirdPartyConfigForBanner } from './types';

const styles = cssMap({
	appButton: {
		borderRadius: token('radius.medium'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		backgroundColor: token('elevation.surface'),
	},
});

interface AppButtonProps {
	appKey: string;
	config: ThirdPartyConfigForBanner;
	onAppClick?: (appKey: string, config: ThirdPartyConfigForBanner) => void;
	testId?: string;
	useOAuthHandlers?: UseOAuthHandlersHook;
	onDismissParent?: () => void;
}

/**
 * Renders the icon for an app, preferring iconBorderless over icon.
 */
function AppIcon({ config }: { config: ThirdPartyConfigForBanner }) {
	const IconComponent = config.iconBorderless || config.icon;

	if (!IconComponent) {
		return null;
	}

	return <IconComponent size="xsmall" label="" />;
}

/**
 * A button component for connecting to a third-party app.
 * Handles OAuth flow when clicked.
 */
export function AppButton({
	appKey,
	config,
	onAppClick,
	testId,
	useOAuthHandlers,
}: AppButtonProps) {
	const { formatMessage } = useIntl();

	const productString = typeof config.displayName === 'string' ? config.displayName : appKey;

	const oauthResult = useOAuthHandlers?.({
		product: appKey,
		successFlagTitle: formatMessage(messages.authorizationSuccess, {
			product: productString,
		}),
		failFlagTitle: formatMessage(messages.authorizationFail, {
			product: productString,
		}),
		outboundAuthUrl: config.outboundAuthUrl || '',
	});

	const handleClick = () => {
		onAppClick?.(appKey, config);

		if (oauthResult?.onAuthorize) {
			oauthResult.onAuthorize('sain-third-party-banner');
		}
	};

	return (
		<Box xcss={styles.appButton}>
			<Button appearance="subtle" spacing="compact" onClick={handleClick} testId={testId}>
				<Inline space="space.075" alignBlock="center">
					<AppIcon config={config} />
					<Text color="color.text.subtle">{config.displayName ?? appKey}</Text>
				</Inline>
			</Button>
		</Box>
	);
}
